import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Target, 
  AlertCircle, 
  Clock, 
  BarChart3,
  CheckCircle,
  XCircle,
  MinusCircle,
  RefreshCw,
  Lock,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Consenso() {
  const { token } = useAuth();
  const [consensus, setConsensus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    checkAccessAndFetch();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchConsensus(), 30000);
    return () => clearInterval(interval);
  }, []);

  const checkAccessAndFetch = async () => {
    try {
      const accessRes = await fetch(`${API_URL}/api/consensus/check-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (accessRes.ok) {
        const accessData = await accessRes.json();
        if (!accessData.has_access) {
          setAccessDenied(true);
          setLoading(false);
          return;
        }
      }
      
      await fetchConsensus();
    } catch (error) {
      console.error('Error checking access:', error);
      setLoading(false);
    }
  };

  const fetchConsensus = async (showRefresh = false) => {
    if (accessDenied) return;
    if (showRefresh) setRefreshing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/consensus/blind`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setConsensus(await response.json());
      } else if (response.status === 403) {
        setAccessDenied(true);
      }
    } catch (error) {
      console.error('Error fetching consensus:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getRecommendationBadge = (rec) => {
    switch (rec) {
      case 'STRONG':
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[hsl(160,84%,39%)]/20 border border-[hsl(160,84%,39%)]/40 rounded-full">
            <CheckCircle className="w-4 h-4 text-[hsl(160,84%,39%)]" />
            <span className="text-sm font-medium text-[hsl(160,84%,39%)]">Señal Fuerte</span>
          </div>
        );
      case 'MODERATE':
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[hsl(45,93%,47%)]/20 border border-[hsl(45,93%,47%)]/40 rounded-full">
            <AlertCircle className="w-4 h-4 text-[hsl(45,93%,47%)]" />
            <span className="text-sm font-medium text-[hsl(45,93%,47%)]">Señal Moderada</span>
          </div>
        );
      case 'INSUFFICIENT_DATA':
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full">
            <MinusCircle className="w-4 h-4 text-white/50" />
            <span className="text-sm font-medium text-white/50">Datos Insuficientes</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-500/40 rounded-full">
            <XCircle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">Sin Consenso</span>
          </div>
        );
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    }) + ' UTC';
  };

  const getTimeRemaining = () => {
    if (!consensus) return '';
    const target = new Date(consensus.target_time);
    const now = new Date();
    const diff = target - now;
    
    if (diff <= 0) return 'Verificando...';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full"></div>
      </div>
    );
  }

  // Access denied - user hasn't done blind session this period
  if (accessDenied) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-2xl p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[hsl(45,93%,47%)]/20 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-[hsl(45,93%,47%)]" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-3">Acceso Restringido</h1>
          
          <p className="text-white/60 mb-6">
            Para ver el consenso colectivo, primero debes completar tu <strong className="text-white">sesión ciega</strong> de este período de 12 horas.
          </p>
          
          <div className="bg-white/5 rounded-xl p-4 mb-6">
            <p className="text-white/50 text-sm">
              El consenso muestra las predicciones agregadas de todos los usuarios que participan en la sesión ciega. 
              Para mantener la integridad del protocolo, solo puedes verlo después de enviar tu propia predicción.
            </p>
          </div>
          
          <Link 
            to="/nueva-sesion"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(198,62%,54%)] hover:bg-[hsl(198,62%,48%)] text-white font-medium rounded-xl transition-colors"
          >
            <Eye className="w-5 h-5" />
            Hacer Sesión Ciega
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Consenso Colectivo</h1>
          <p className="text-white/50">
            Predicción agregada de todos los usuarios en la sesión ciega actual
          </p>
        </div>
        <button
          onClick={() => fetchConsensus(true)}
          disabled={refreshing}
          className="p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors disabled:opacity-50"
          data-testid="refresh-consensus"
        >
          <RefreshCw className={`w-5 h-5 text-white/70 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Main Consensus Card */}
      <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-2xl p-6 mb-6">
        {/* Target Info */}
        <div className="flex items-center justify-between mb-6 pb-6 border-b border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(198,62%,54%)]/20 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-[hsl(198,62%,54%)]" />
            </div>
            <div>
              <p className="text-white font-semibold text-lg">{consensus?.symbol}</p>
              <p className="text-white/50 text-sm">
                {consensus?.instrument_type === 'crypto' ? 'Criptomoneda' : 'Forex'}
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="flex items-center gap-2 text-white/50 text-sm mb-1">
              <Clock className="w-4 h-4" />
              <span>Verificación en</span>
            </div>
            <p className="text-white font-medium">{getTimeRemaining()}</p>
            <p className="text-white/40 text-xs">{formatTime(consensus?.target_time)}</p>
          </div>
        </div>

        {/* Consensus Direction */}
        <div className="text-center mb-8">
          {consensus?.consensus_direction === 'UP' ? (
            <div className="inline-flex flex-col items-center">
              <div className="w-24 h-24 rounded-full bg-[hsl(160,84%,39%)]/20 flex items-center justify-center mb-3">
                <TrendingUp className="w-12 h-12 text-[hsl(160,84%,39%)]" />
              </div>
              <p className="text-4xl font-bold text-[hsl(160,84%,39%)] mb-1">SUBIDA</p>
              <p className="text-white/50">{consensus?.consensus_percentage}% de acuerdo</p>
            </div>
          ) : consensus?.consensus_direction === 'DOWN' ? (
            <div className="inline-flex flex-col items-center">
              <div className="w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center mb-3">
                <TrendingDown className="w-12 h-12 text-red-400" />
              </div>
              <p className="text-4xl font-bold text-red-400 mb-1">BAJADA</p>
              <p className="text-white/50">{consensus?.consensus_percentage}% de acuerdo</p>
            </div>
          ) : (
            <div className="inline-flex flex-col items-center">
              <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mb-3">
                <MinusCircle className="w-12 h-12 text-white/40" />
              </div>
              <p className="text-2xl font-bold text-white/40 mb-1">SIN CONSENSO</p>
              <p className="text-white/50">Esperando más predicciones</p>
            </div>
          )}
        </div>

        {/* Recommendation */}
        <div className="flex justify-center mb-6">
          {getRecommendationBadge(consensus?.recommendation)}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Users className="w-4 h-4 text-white/50" />
            </div>
            <p className="text-2xl font-bold text-white">{consensus?.total_participants}</p>
            <p className="text-xs text-white/50">Participantes</p>
          </div>
          
          <div className="bg-[hsl(160,84%,39%)]/10 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-[hsl(160,84%,39%)]" />
            </div>
            <p className="text-2xl font-bold text-[hsl(160,84%,39%)]">{consensus?.up_votes}</p>
            <p className="text-xs text-white/50">Votos UP</p>
          </div>
          
          <div className="bg-red-500/10 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-400">{consensus?.down_votes}</p>
            <p className="text-xs text-white/50">Votos DOWN</p>
          </div>
          
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Target className="w-4 h-4 text-white/50" />
            </div>
            <p className="text-2xl font-bold text-white">{consensus?.avg_confidence}</p>
            <p className="text-xs text-white/50">Conf. Promedio</p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-[hsl(252,37%,60%)]/10 border border-[hsl(252,37%,60%)]/30 rounded-xl p-5">
        <div className="flex gap-4">
          <AlertCircle className="w-5 h-5 text-[hsl(252,37%,60%)] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-white mb-2">Sobre el Consenso Multi-Trial</h3>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Todos los usuarios de la sesión ciega predicen el <strong className="text-white">mismo activo</strong> para la <strong className="text-white">misma hora</strong></li>
              <li>• El consenso se calcula agregando todas las predicciones</li>
              <li>• <strong className="text-[hsl(160,84%,39%)]">Señal Fuerte</strong>: ≥70% de acuerdo con ≥3 participantes</li>
              <li>• <strong className="text-[hsl(45,93%,47%)]">Señal Moderada</strong>: ≥60% de acuerdo con ≥2 participantes</li>
              <li>• Los PASES no se cuentan en el cálculo</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recent Predictions */}
      {consensus?.predictions?.length > 0 && (
        <div className="mt-6 bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6">
          <h3 className="font-medium text-white mb-4">Predicciones Recientes</h3>
          <div className="space-y-2">
            {consensus.predictions.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <span className="text-white/70">{p.user_name}</span>
                <div className="flex items-center gap-3">
                  <span className={`font-medium ${
                    p.predicted_dir === 'UP' ? 'text-[hsl(160,84%,39%)]' : 'text-red-400'
                  }`}>
                    {p.predicted_dir}
                  </span>
                  <span className="text-white/40 text-sm">Conf: {p.confidence}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
